import palette from "assets/theme/base/palette";
import React, { useEffect, useState } from "react";
import { call } from "services/axiosService/core";
import Dialog from "@mui/material/Dialog";
import Divider from "@mui/material/Divider";
import Icon from "@mui/material/Icon";
import * as Sentry from "@sentry/browser";
import { useMaterialUIController } from "context";
import PaymentSetupWrapper from "./PaymentSetupWrapper";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import { Chip, CircularProgress } from "@mui/material";
import ConfirmDialog from "examples/Dialog/ConfirmDialog";

const BRAND_GRADIENTS = {
  visa:       "linear-gradient(135deg, #1a1f71, #3a7bd5)",
  mastercard: "linear-gradient(135deg, #eb001b, #f79e1b)",
  amex:       "linear-gradient(135deg, #007bc1, #00aff0)",
  discover:   "linear-gradient(135deg, #e65c00, #f9d423)",
};
const cardGradient = (brand) => BRAND_GRADIENTS[brand?.toLowerCase()] || "linear-gradient(135deg, #434343, #000)";

const ManagePaymentMethods = ({
  handleCloseManagePaymentMethodModal,
  setOpenSnackbar,
  setSeverity,
  setAlertMessage,
  fromOnboarding = false,
  t,
}) => {
  const [paymentMethods, setPaymentMethods]     = useState([]);
  const [defaultPaymentId, setDefaultPaymentId] = useState(null);
  const [step, setStep]                         = useState(1);
  const [deleting, setDeleting]                 = useState(false);
  const [paymentId, setPaymentId]               = useState(null);
  const [openAddPaymentMethodModal, setOpenAddPaymentMethodModal] = useState(false);
  const [controller] = useMaterialUIController();
  const { profile } = controller;

  useEffect(() => { loadPaymentMethods(); }, []);

  const isCardExpired = (expMonth, expYear) => {
    const now = new Date();
    return expYear < now.getFullYear() || (expYear === now.getFullYear() && expMonth < now.getMonth() + 1);
  };

  const loadPaymentMethods = async () => {
    setStep(1);
    try {
      const response = await call(`payments/get-payment-methods/${profile.id}`, "GET", {});
      if (response.status === 200) {
        const methods   = response?.data?.payment_methods || [];
        const defaultId = response?.data?.default_payment_id || null;
        const sorted = defaultId
          ? [...methods.filter((m) => m.id === defaultId), ...methods.filter((m) => m.id !== defaultId)]
          : methods;
        setPaymentMethods(sorted);
        setDefaultPaymentId(defaultId);
        setStep(2);
      } else {
        setOpenSnackbar(true); setSeverity("error");
        setAlertMessage("Error while loading payment methods");
      }
    } catch (error) { Sentry.captureException(error); }
  };

  const handleDeletePaymentMethod = () => {
    setDeleting(true);
    call(`payments/delete-payment-method/${paymentId}/${profile.id}`, "DELETE", {})
      .then((response) => {
        setDeleting(false);
        if (response.status === 200) {
          setPaymentId(null); loadPaymentMethods(); setStep(2);
          setOpenSnackbar(true); setSeverity("success");
          setAlertMessage("Payment method deleted successfully");
        } else {
          setStep(2); setOpenSnackbar(true); setSeverity("error");
          setAlertMessage("Error while deleting payment method");
        }
      })
      .catch((error) => { Sentry.captureException(error); setDeleting(false); });
  };

  const handleSetDefaultPaymentMethod = async (id) => {
    try {
      const response = await call("payments/set-default-payment-method", "PUT", { body: { user_id: profile.id, payment_id: id } });
      setOpenSnackbar(true);
      if (response.status === 200) {
        setSeverity("success"); setAlertMessage("Default payment method updated successfully");
        loadPaymentMethods();
      } else {
        setSeverity("error"); setAlertMessage("Failed to update default payment method");
      }
    } catch (error) {
      Sentry.captureException(error);
      setOpenSnackbar(true); setSeverity("error");
      setAlertMessage("Error occurred while setting default payment method");
    }
  };

  return (
    <>
      {/* Delete confirmation */}
      {step === 3 && (
        <ConfirmDialog
          open={true}
          onClose={() => { setPaymentId(null); setStep(2); }}
          onConfirm={handleDeletePaymentMethod}
          title={t("alerts.addPayment.deletePaymentMethod")}
          message={t("alerts.addPayment.deletePaymentMethodMessage")}
          confirmLabel={t("buttonTitles.delete")}
          cancelLabel={t("buttonTitles.cancel") || "Cancel"}
          dangerous
          loading={deleting}
          icon="credit_card"
        />
      )}

      {/* Main dialog */}
      {!openAddPaymentMethodModal && (
        <Dialog
          open={step !== 3}
          onClose={handleCloseManagePaymentMethodModal}
          fullWidth
          maxWidth="sm"
          PaperProps={{ sx: { borderRadius: "24px", overflowX: "hidden" } }}
        >
          {/* Circle close button */}
          <MDBox
            onClick={handleCloseManagePaymentMethodModal}
            sx={{
              position: "absolute", top: 12, right: 12, zIndex: 10,
              width: 36, height: 36, borderRadius: "50%",
              bgcolor: palette.neutral[100], border: `1px solid ${palette.neutral[200]}`,
              display: "flex", alignItems: "center", justifyContent: "center",
              cursor: "pointer", transition: "all 180ms ease",
              "&:hover": { bgcolor: palette.neutral[200] },
            }}
          >
            <Icon sx={{ fontSize: "18px !important", color: palette.neutral[600] }}>close</Icon>
          </MDBox>

          <MDBox sx={{ px: 4, pt: 5, pb: 4 }}>
            {/* Header */}
            <MDBox textAlign="center" mb={3}>
              <MDBox
                sx={{
                  width: 80, height: 80, borderRadius: "50%",
                  bgcolor: "#4caf5018",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  mx: "auto", mb: 2,
                }}
              >
                <Icon sx={{ fontSize: "40px !important", color: "#4caf50" }}>credit_card</Icon>
              </MDBox>
              <MDTypography variant="h5" fontWeight="bold" mb={0.5}>
                {t("headings.managePaymentMethods")}
              </MDTypography>
              <MDTypography variant="body2" color="text.secondary">
                Manage your saved payment methods
              </MDTypography>
            </MDBox>

            {/* Loading */}
            {step === 1 && (
              <MDBox display="flex" justifyContent="center" alignItems="center" py={6}>
                <CircularProgress sx={{ color: palette.brand[500] }} />
              </MDBox>
            )}

            {/* Payment methods list */}
            {step === 2 && (
              <>
                {/* Add button */}
                <MDBox display="flex" justifyContent="flex-end" gap={1.5} mb={2}>
                  {paymentMethods.length === 0 && (
                    <MDButton
                      variant="outlined"
                      onClick={() => setStep(4)}
                      sx={{
                        borderRadius: "12px", textTransform: "none",
                        borderColor: palette.neutral[300], color: palette.neutral[600],
                      }}
                    >
                      {t("buttonTitles.doItLater") || "Do it later"}
                    </MDButton>
                  )}
                  <MDButton
                    variant="contained"
                    startIcon={<Icon>add</Icon>}
                    onClick={() => setOpenAddPaymentMethodModal(true)}
                    sx={{
                      borderRadius: "12px", textTransform: "none",
                      bgcolor: palette.brand[500], color: "#fff",
                      "&:hover": { bgcolor: palette.brand[600] },
                    }}
                  >
                    {t("buttonTitles.addPaymentMethod")}
                  </MDButton>
                </MDBox>

                {/* Empty state */}
                {paymentMethods.length === 0 ? (
                  <MDBox
                    sx={{
                      border: `2px dashed ${palette.neutral[200]}`,
                      borderRadius: "16px", py: 5, textAlign: "center",
                      bgcolor: palette.neutral[50],
                    }}
                  >
                    <MDBox sx={{ width: 56, height: 56, borderRadius: "50%", bgcolor: palette.neutral[100], display: "flex", alignItems: "center", justifyContent: "center", mx: "auto", mb: 1.5 }}>
                      <Icon sx={{ fontSize: "28px !important", color: palette.neutral[400] }}>credit_card_off</Icon>
                    </MDBox>
                    <MDTypography variant="subtitle1" fontWeight="bold" color="text.secondary" mb={0.5}>
                      {t("alerts.addPayment.noPaymentMethodTitle") || "No Payment Methods"}
                    </MDTypography>
                    <MDTypography variant="body2" color="text.secondary">
                      {t("alerts.addPayment.noPaymentMethod") || "Add a payment method to get started."}
                    </MDTypography>
                  </MDBox>
                ) : (
                  /* Card list */
                  <MDBox
                    sx={{ border: `1px solid ${palette.neutral[200]}`, borderRadius: "16px", overflow: "hidden" }}
                  >
                    {paymentMethods.map((pm, i) => {
                      const expired = isCardExpired(pm.card.exp_month, pm.card.exp_year);
                      const isDefault = defaultPaymentId === pm.id;
                      return (
                        <MDBox key={pm.id}>
                          <MDBox display="flex" alignItems="center" gap={2} px={2.5} py={2}>
                            {/* Mini card graphic */}
                            <MDBox
                              sx={{
                                width: 52, height: 36, borderRadius: "8px", flexShrink: 0,
                                background: cardGradient(pm.card.brand),
                                display: "flex", alignItems: "center", justifyContent: "center",
                              }}
                            >
                              <Icon sx={{ color: "#fff", fontSize: "20px !important" }}>credit_card</Icon>
                            </MDBox>

                            {/* Card details */}
                            <MDBox flex={1}>
                              <MDBox display="flex" alignItems="center" gap={1} mb={0.25}>
                                <MDTypography variant="body2" fontWeight="bold" sx={{ textTransform: "uppercase" }}>
                                  {pm.card.brand}
                                </MDTypography>
                                <MDTypography variant="body2" color="text.secondary">
                                  •••• {pm.card.last4}
                                </MDTypography>
                                {isDefault && (
                                  <Chip label="Default" size="small" sx={{ bgcolor: "#e8f5e9", color: "#4caf50", fontWeight: "bold", fontSize: "0.65rem", height: 20 }} />
                                )}
                                {expired && (
                                  <Chip label="Expired" size="small" sx={{ bgcolor: "#fdecea", color: "#f44336", fontWeight: "bold", fontSize: "0.65rem", height: 20 }} />
                                )}
                              </MDBox>
                              <MDTypography variant="caption" color="text.secondary">
                                Expires {pm.card.exp_month}/{pm.card.exp_year}
                              </MDTypography>
                            </MDBox>

                            {/* Actions */}
                            <MDBox display="flex" alignItems="center" gap={1}>
                              {!isDefault && (
                                <MDButton
                                  variant="outlined"
                                  size="small"
                                  onClick={() => handleSetDefaultPaymentMethod(pm.id)}
                                  sx={{
                                    borderRadius: "8px", textTransform: "none", fontSize: "0.75rem",
                                    borderColor: palette.neutral[300], color: "text.secondary", py: 0.5, px: 1.5,
                                    "&:hover": { borderColor: palette.brand[400], color: palette.brand[500] },
                                  }}
                                >
                                  Set default
                                </MDButton>
                              )}
                              <MDBox
                                onClick={() => { setPaymentId(pm.id); setStep(3); }}
                                sx={{
                                  width: 32, height: 32, borderRadius: "8px",
                                  bgcolor: "#fdecea", display: "flex",
                                  alignItems: "center", justifyContent: "center",
                                  cursor: "pointer",
                                  "&:hover": { bgcolor: "#f44336", "& .MuiIcon-root": { color: "#fff" } },
                                }}
                              >
                                <Icon sx={{ fontSize: "16px !important", color: "#f44336" }}>delete</Icon>
                              </MDBox>
                            </MDBox>
                          </MDBox>
                          {i < paymentMethods.length - 1 && <Divider sx={{ m: 0 }} />}
                        </MDBox>
                      );
                    })}
                  </MDBox>
                )}
              </>
            )}

            {/* Do it later */}
            {step === 4 && (
              <MDBox>
                <MDBox
                  sx={{
                    bgcolor: palette.brand[50], border: `1px solid ${palette.brand[200]}`,
                    borderRadius: "16px", p: 3, textAlign: "center", mb: 3,
                  }}
                >
                  <MDBox sx={{ width: 56, height: 56, borderRadius: "50%", bgcolor: palette.brand[100], display: "flex", alignItems: "center", justifyContent: "center", mx: "auto", mb: 2 }}>
                    <Icon sx={{ fontSize: "28px !important", color: palette.brand[500] }}>schedule</Icon>
                  </MDBox>
                  <MDTypography variant="h6" fontWeight="bold" sx={{ color: palette.brand[600] }} mb={1}>
                    {t("alerts.addPayment.doItLaterTitle") || "No problem!"}
                  </MDTypography>
                  <MDTypography variant="body2" sx={{ color: palette.neutral[700], lineHeight: 1.7 }}>
                    {t("alerts.addPayment.doItLaterMessage") ||
                      "You have 14 days to add a payment method. You can do this anytime from your profile under Manage Payment Methods."}
                  </MDTypography>
                </MDBox>
                <MDButton
                  variant="contained"
                  fullWidth
                  onClick={handleCloseManagePaymentMethodModal}
                  sx={{
                    borderRadius: "12px", py: 1.5, textTransform: "none",
                    bgcolor: palette.brand[500], color: "#fff",
                    "&:hover": { bgcolor: palette.brand[600] },
                  }}
                >
                  {t("buttonTitles.close") || "Close"}
                </MDButton>
              </MDBox>
            )}
          </MDBox>
        </Dialog>
      )}

      {openAddPaymentMethodModal && (
        <PaymentSetupWrapper
          openAddPaymentMethodModal={openAddPaymentMethodModal}
          handleCloseAddPaymentMethodModal={() => { loadPaymentMethods(); setOpenAddPaymentMethodModal(false); }}
          setOpenSnackbar={setOpenSnackbar}
          setAlertMessage={setAlertMessage}
          setSeverity={setSeverity}
          addNewPaymentMethod={true}
          childDetails={profile}
          t={t}
        />
      )}
    </>
  );
};

export default ManagePaymentMethods;
